import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { ISupportEmployee } from '@cbgms/api'
import EmployeeForm from './EmployeeForm'
import { loadEmployee, loadEmployeeSkills, loadEmployeeStatus, updateEmployee } from '../state/actions'
import { Locales } from '@carsys/enums/locales'
import { EmailOptions } from '@cbgms/api/modules/enums'
import { useNotifier } from '@cbgms/base/notifier'
import { Loader } from '@carsys/ui'

interface IEditEmployee extends ISupportEmployee {
  Locale?: Locales
  EmailAddressSelected?: EmailOptions
  EmailAddressOther?: string
}

interface RouteParams {
  companyUUID?: string
  employeeUUID?: string
  shopID?: string
}

const EditEmployeeForm = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const notifier = useNotifier()
  const { employeeUUID, companyUUID, shopID } = useParams<RouteParams>()
  const [initialValues, setInitialValues] = useState<IEditEmployee | null>(null)

  const redirectURL = React.useMemo(() => {
    if (shopID === 'new') {
      return `/companies/${companyUUID}/shops/new#employees`
    }
    return `/companies/${companyUUID}/shops/edit/${shopID}#employees`
  }, [shopID])

  const loadEmployeeData = async () => {
    if (!employeeUUID) return

    const { Data: employee } = await dispatch(loadEmployee(employeeUUID))
    const editedEmployee: IEditEmployee = employee
    try {
      const { Data: employeeStatus } = await dispatch(loadEmployeeStatus(employeeUUID))
      editedEmployee.Locale = employeeStatus.Locale
      editedEmployee.EmailAddressSelected =
        employeeStatus.EmailAddress === employee.EmailAddressWork ? EmailOptions.Work : EmailOptions.Other
      editedEmployee.EmailAddressOther = employeeStatus.EmailAddress
    } catch {
      // it's okay if this catches, it just means the user hasn't been created yet because its a new employee
      // it'll just render the empty create user form
    }
    setInitialValues(employee)
  }

  useEffect(() => {
    loadEmployeeData()
  }, [employeeUUID])

  useEffect(() => {
    if (companyUUID) {
      dispatch(loadEmployeeSkills(companyUUID))
    }
  }, [companyUUID])

  const onSubmit = async (values: any) => {
    if (!employeeUUID) return

    const data = {
      ...values,
      CompanyUUID: companyUUID,
      CurrentShop: shopID
    }

    await dispatch(updateEmployee(employeeUUID, data))
  }

  const onSubmitAndDone = async (values: any) => {
    if (!employeeUUID) return ''

    const data = {
      ...values,
      CompanyUUID: companyUUID,
      CurrentShop: shopID
    }

    await dispatch(updateEmployee(employeeUUID, data))

    return redirectURL
  }

  const onSubmitSuccess = async () => {
    notifier.success(t('setting_employee.successfully_updated'))
  }

  if (!initialValues) return <Loader loading />

  return (
    <EmployeeForm
      form='employee_detail'
      initialValues={initialValues}
      onSubmit={onSubmit}
      onSubmitSuccess={onSubmitSuccess}
      onSubmitAndDone={onSubmitAndDone}
      onUserDeletion={loadEmployeeData}
      redirectToURL={redirectURL}
    />
  )
}

export default EditEmployeeForm
